import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, ImagesCardsContent, SectionMargins } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaFileDropzone,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { useShopImageUpload } from "./useShopImageUpload";

interface ImagesCardsFormProps {
  onSubmit: (data: ImagesCardsFormValues) => void;
  onCancel: () => void;
  defaultValues?: ImagesCardsContent;
  isLoading?: boolean;
}

export const ImagesCardsForm = ({ onSubmit, onCancel, defaultValues, isLoading }: ImagesCardsFormProps) => {
  const { uploadImages } = useShopImageUpload();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.LAYOUT]: defaultValues?.layout ?? "2-columns-2-images-in-second",
      [FORM_NAMES.HOVER_COLOR]: defaultValues?.hoverColor ?? "#4d5d6d",
      [FORM_NAMES.IMAGES]: defaultValues?.images?.map((img) => ({ title: img.title, titleTag: img.titleTag ?? "h3", src: img.src, href: img.href })) ?? [{ title: "", titleTag: "h3", src: "", href: "" }],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_NAMES.IMAGES,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const uploadedImages = await uploadImages(data.images);
    const formattedData: ImagesCardsFormValues = {
      animation: data.animation,
      layout: data.layout,
      hoverColor: data.hoverColor,
      images: uploadedImages.map((img) => ({
        title: img.title,
        titleTag: img.titleTag as any,
        src: img.src,
        href: img.href,
      })),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addImage = () => {
    append({ title: "", titleTag: "h3", src: "", href: "" } as any);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-3">
          <PaziaSelect
            name={FORM_NAMES.ANIMATION}
            label="Animacja"
            required
            options={ANIMATION_OPTIONS}
          />
          <PaziaSelect
            name={FORM_NAMES.LAYOUT}
            label="Układ"
            required
            options={LAYOUT_OPTIONS}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Kolor hover
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                {...methods.register(FORM_NAMES.HOVER_COLOR)}
                className="h-9 w-14 cursor-pointer rounded border border-slate-200 dark:border-slate-600"
              />
              <PaziaInput name={FORM_NAMES.HOVER_COLOR} className="flex-1" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Obrazy</h4>
            <button
              type="button"
              onClick={addImage}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj obraz
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak obrazów. Kliknij "Dodaj obraz", aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Obraz #{index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń obraz"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <PaziaInput
                      name={`${FORM_NAMES.IMAGES}.${index}.title`}
                      label="Tytuł"
                      required
                    />
                    <PaziaInput
                      name={`${FORM_NAMES.IMAGES}.${index}.href`}
                      label="Link (href)"
                      required
                    />
                  </div>
                  <HeadingTagSelect name={`${FORM_NAMES.IMAGES}.${index}.titleTag`} label="Tag tytułu" />
                  <div className="mt-3 [&_img]:max-h-48">
                    <PaziaFileDropzone
                      name={`${FORM_NAMES.IMAGES}.${index}.src`}
                      label="Obraz"
                      type="image"
                      single
                      preview="image"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  ANIMATION: "animation",
  LAYOUT: "layout",
  HOVER_COLOR: "hoverColor",
  IMAGES: "images",
} as const;

const LAYOUT_OPTIONS = [
  { value: "2-columns-2-images-in-second", label: "2 kolumny (2 obrazy w drugiej)" },
];

const imageSchema = yup.object({
  title: yup.string().required("Tytuł jest wymagany"),
  titleTag: yup.string(),
  src: yup.mixed().required("Obraz jest wymagany"),
  href: yup.string().required("Link jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.LAYOUT]: yup.string().required("Układ jest wymagany"),
    [FORM_NAMES.HOVER_COLOR]: yup.string().required("Kolor hover jest wymagany"),
    [FORM_NAMES.IMAGES]: yup.array().of(imageSchema).min(1, "Dodaj przynajmniej jeden obraz").required(),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  animation: string;
  layout: string;
  hoverColor: string;
  images: Array<{ title: string; titleTag?: string; src: string | File; href: string }>;
  margins: SectionMargins;
}

export interface ImagesCardsFormValues {
  animation: string;
  layout: string;
  hoverColor: string;
  images: Array<{
    title: string;
    titleTag?: string;
    src: string;
    href: string;
  }>;
  margins?: SectionMargins;
}
